import { useState, useEffect, useMemo, useCallback } from "react";
import { Card } from "../components/ui/card";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
const CATEGORY_ORDER = ["general", "deluxe", "executive", "premium"];
export default function GalleryPage() {

  // Import images
  const allImages = useMemo(() => {

    const importAll = (r) =>
      r.keys().map((key) => ({
        src: r(key),
        category: key.split("/")[1],
        name: key.split("/")[2]
      }));

    return importAll(
      require.context("../assets/gallery", true, /\.(JPG|png|jpe?g|webp)$/)
    );

  }, []);


  const categories = useMemo(() => {

    const available = new Set(
      allImages.map(img => img.category)
    );

    return [
      "all",
      ...CATEGORY_ORDER.filter(cat => available.has(cat))
    ];

  }, [allImages]);

  const [activeFilter, setActiveFilter] = useState("all");
  const [filteredImages, setFilteredImages] = useState(allImages);
  const [selectedIndex, setSelectedIndex] = useState(null);


  // Filter images safely
  useEffect(() => {

    if (activeFilter === "all") {
      setFilteredImages(allImages);
    } else {
      setFilteredImages(
        allImages.filter(img => img.category === activeFilter)
      );
    }

  }, [activeFilter, allImages]);


  const openModal = useCallback((index) => {
    setSelectedIndex(index);
  }, []);


  const closeModal = useCallback(() => {
    setSelectedIndex(null);
  }, []);


  const showPrev = useCallback(() => {

    setSelectedIndex(prev =>
      prev === 0 ? filteredImages.length - 1 : prev - 1
    );

  }, [filteredImages]);


  const showNext = useCallback(() => {

    setSelectedIndex(prev =>
      prev === filteredImages.length - 1 ? 0 : prev + 1
    );

  }, [filteredImages]);


  // Keyboard navigation FIXED
  useEffect(() => {

    const handleKey = (e) => {

      if (selectedIndex === null) return;

      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();

    };

    window.addEventListener("keydown", handleKey);

    return () => window.removeEventListener("keydown", handleKey);

  }, [selectedIndex, closeModal, showPrev, showNext]);


  return (

    <div className="min-h-screen py-16 bg-gradient-to-b from-white to-[#FFF8DC]">

      <div className="container mx-auto px-4">

        {/* <h1 className="text-4xl md:text-5xl font-serif font-bold text-center text-[#8B6F47] mb-8">
          Gallery
        </h1> */}


        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">

          {categories.map(category => (

            <button
              key={category}
              onClick={() => setActiveFilter(category)}
              className={`px-5 py-2 rounded-full border transition duration-300 ${activeFilter === category
                ? "bg-[#8B6F47] text-white border-[#8B6F47]"
                : "bg-white text-[#8B6F47] border-[#E6D5C3] hover:bg-[#FFF8DC]"
                }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}

            </button>

          ))}

        </div>


        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">

          {filteredImages.map((image, index) => (

            <Card
              key={index}
              onClick={() => openModal(index)}
              className="cursor-pointer overflow-hidden rounded-xl border border-[#E6D5C3] hover:shadow-xl transition duration-300"
            >

              <img
                src={image.src}
                alt=""
                className="w-full h-64 object-cover hover:scale-110 transition duration-500"
              />

            </Card>

          ))}

        </div>

      </div>


      {/* Modal */}
      {selectedIndex !== null && (

        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">

          <button
            onClick={closeModal}
            className="absolute top-6 right-6 text-white"
          >
            <X size={36} />
          </button>


          <button
            onClick={showPrev}
            className="absolute left-6 text-white"
          >
            <ChevronLeft size={48} />
          </button>


          <img
            src={filteredImages[selectedIndex].src}
            alt=""
            className="max-h-[90%] max-w-[90%]"
          />


          <button
            onClick={showNext}
            className="absolute right-6 text-white"
          >
            <ChevronRight size={48} />
          </button>

        </div>

      )}

    </div>
  );
}
